import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFeature } from '@mafc/appshell';

import FEATURES_CONFIG from 'config/features';
import { IAppState } from 'config/redux/reducers';
import { EShipmentTabs, ICnc } from 'pages/Checkout/types/cnc.types';
import { setActiveTab, setCNCTabClicked } from 'pages/Checkout/redux/actions/cnc.action';
import { setSelectedStore } from '../../PickupStores/redux/PickupStores.actions';
import useShipments from 'services/shipments/hooks/useShipments.hook';
import { EShipmentTypes } from 'services/shipments/types/shipments.types';

const useClickAndCollect = (): {
  isEnableClickAndCollect: boolean;
  clickAndCollectState: ICnc;
  isCNCVisible: boolean;
  enableCNCTabClicked: () => void;
  disableCNCTabClicked: () => void;
  setHomeDeliveryTab: () => void;
  initSelectedStoreHandler: () => void;
} => {
  const {
    cart: { cartData },
    checkout: { shipment },
    shipments,
  } = useSelector((state: IAppState) => state);

  const dispatch = useDispatch();
  const isCNCEnabled = useFeature('click-n-collect').on;

  const setHomeDeliveryTab = useCallback(
    () => dispatch(setActiveTab(EShipmentTabs.HOME_DELIVERY_TAB)),
    [dispatch]
  );
  const disableCNCTabClicked = useCallback(() => dispatch(setCNCTabClicked(false)), [dispatch]);
  const enableCNCTabClicked = useCallback(() => dispatch(setCNCTabClicked(true)), [dispatch]);
  const {
    clickAndCollect: { store: shipmentStore },
  } = useShipments();
  const initSelectedStoreHandler = useCallback(() => {
    if (shipmentStore) {
      dispatch(setSelectedStore(shipmentStore.name, shipmentStore));
    }
  }, [dispatch, shipmentStore]);

  const isSingleStandardShipmentExists = !!(
    shipments &&
    shipments.length === 1 &&
    shipments.find(shipmentItem => shipmentItem.shipmentType === EShipmentTypes.STANDARD_FOOD)
  );

  return useMemo(
    () => ({
      isEnableClickAndCollect:
        isCNCEnabled &&
        FEATURES_CONFIG.settings.clickAndCollect &&
        (cartData.clickNCollect || {}).isEnable &&
        isSingleStandardShipmentExists,
      clickAndCollectState: shipment.cnc,
      isCNCVisible: shipment.cnc.activeTab === EShipmentTabs.CNC_TAB,
      enableCNCTabClicked,
      disableCNCTabClicked,
      setHomeDeliveryTab,
      initSelectedStoreHandler,
    }),

    [
      cartData,
      shipment.cnc,
      isSingleStandardShipmentExists,
      isCNCEnabled,
      enableCNCTabClicked,
      disableCNCTabClicked,
      setHomeDeliveryTab,
      initSelectedStoreHandler,
    ]
  );
};

export default useClickAndCollect;
